/**
 * DOM manipulation utilities.
 * Provides safe access to HTML document and element attributes.
 */

'use strict';

/**
 * Reference to the HTML document element for attribute operations.
 */
const htmlElement: HTMLElement = document.documentElement;

/**
 * Get an attribute value from the document root element.
 * 
 * @param name - Attribute name
 * @returns Attribute value or null if not set
 */
export function getAttribute(name: string): string | null {
    return htmlElement.getAttribute(name);
}

/**
 * Set an attribute on the document root element.
 * 
 * @param name - Attribute name
 * @param value - Attribute value
 */
export function setAttribute(name: string, value: string): void {
    htmlElement.setAttribute(name, value);
}

/**
 * Remove an attribute from the document root element.
 * 
 * @param name - Attribute name
 */
export function removeAttribute(name: string): void {
    htmlElement.removeAttribute(name);
}

/**
 * Set the indeterminate state of a checkbox or radio input.
 * 
 * @param element - Input element
 * @param isIndeterminate - Whether to set indeterminate state
 */
export function setIndeterminate(
    element: HTMLInputElement | null,
    isIndeterminate: boolean
): void {
    if (element) {
        element.indeterminate = !!isIndeterminate;
    }
}

/**
 * Check if the operating system prefers dark color scheme.
 * 
 * @returns True if dark mode is preferred
 */
export function prefersDarkMode(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }

    try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
        return false;
    }
}

/**
 * Write text to the system clipboard.
 * 
 * @param text - Text to write
 * @returns Promise resolving to true if successful
 */
export async function writeClipboardText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text ?? '');
        return true;
    } catch {
        return false;
    }
}

/**
 * Read text from the system clipboard.
 * 
 * @returns Promise resolving to clipboard text or null if failed
 */
export async function readClipboardText(): Promise<string | null> {
    try {
        return await navigator.clipboard.readText();
    } catch {
        return null;
    }
}
